import template from "./employee_selection_builder.html";
import en from "./en.json";
import es from "./es.json";
import _ from "lodash";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { SID_LOOKUP } from "../../../../app.constants";
import { getLatestMembership } from "../../../../common/common/common.utils";
import { serializeEmployeeSelection } from "./employee_selection.rules";

const RULE_CREW = "crew";
const RULE_CREW_HISTORY = "crewHistory";
const RULE_WORKER = "worker";
const RULE_INACTIVE_WORKER = "inactiveWorker";
const RULE_ONBOARDING = "latestOnboarding";
const RULE_HOUSING_SITE = "housingSite";
const RULE_GROWER_MEMBERSHIP_ACTIVE = "growerMembershipActive";

class EmployeeSelectionBuilderController {
    /* @ngInject */
    constructor(
        $q,
        GroupService,
        MembershipService,
        HousingAssignmentService,
        HousingSiteService,
        GrowerMembershipService,
        FarmService,
        DocumentPackageRequestService,
    ) {
        this.$q = $q;
        this.GroupService = GroupService;
        this.MembershipService = MembershipService;
        this.HousingAssignmentService = HousingAssignmentService;
        this.HousingSiteService = HousingSiteService;
        this.GrowerMembershipService = GrowerMembershipService;
        this.FarmService = FarmService;
        this.DocumentPackageRequestService = DocumentPackageRequestService;
        this.RULE_CREW = RULE_CREW;
        this.RULE_CREW_HISTORY = RULE_CREW_HISTORY;
        this.RULE_WORKER = RULE_WORKER;
        this.RULE_INACTIVE_WORKER = RULE_INACTIVE_WORKER;
        this.RULE_ONBOARDING = RULE_ONBOARDING;
        this.RULE_HOUSING_SITE = RULE_HOUSING_SITE;
        this.RULE_GROWER_MEMBERSHIP_ACTIVE = RULE_GROWER_MEMBERSHIP_ACTIVE;
        this.includeRules = [];
        this.excludeRules = [];
        this.preview = [];
        this._specByType = {}; // populated in $onInit
        this._emptyRuleData = {};
        this._selectionRequestId = 0;
        this._recomputeRequestId = 0;
        this.preselectedGrowerMembershipByUserSid = {};
        this.preselectionLoading = true;
        this.preselectionLoadFailed = false;
        this.growerMembershipActiveOptions = [
            {
                value: true,
                labelTranslation: "employeeSelectionBuilder.ACTIVE",
            },
            {
                value: false,
                labelTranslation: "employeeSelectionBuilder.INACTIVE",
            },
        ];
    }

    $onInit() {
        let ctrl = this;
        ctrl.setRecomputing(true);

        if (_.isUndefined(ctrl.showPreview)) {
            ctrl.showPreview = !ctrl.rulesOnly;
        }

        ctrl.buildRuleSections();
        ctrl.buildRuleSpecs();
        ctrl.initialized = true;

        return ctrl.loadInitialSelection();
    }

    $onChanges(changes) {
        if (this.initialized && this.rulesOnly && changes.selectionRules) {
            return this.loadInitialSelection();
        }
    }

    loadInitialSelection() {
        let ctrl = this;
        ctrl.preselectionLoading = true;
        ctrl.preselectionLoadFailed = false;
        ctrl.setRecomputing(true);

        let preselectionPromise = ctrl.$q.when();
        if (ctrl.rulesOnly) {
            ctrl.includeRules = _.cloneDeep(ctrl.selectionRules.include);
            ctrl.excludeRules = _.cloneDeep(ctrl.selectionRules.exclude);
            ctrl.buildRuleSections();
        } else if (
            !_.isUndefined(ctrl.preselectedGrowerMembershipSids) &&
            ctrl.preselectedGrowerMembershipSids.length
        ) {
            preselectionPromise = ctrl.GrowerMembershipService.several(
                ctrl.preselectedGrowerMembershipSids,
            ).then(function (growerMemberships) {
                ctrl.preselectedGrowerMembershipByUserSid = _.keyBy(
                    growerMemberships,
                    "user",
                );
                const [activeMemberships, inactiveMemberships] = _.partition(
                    growerMemberships,
                    "active",
                );
                ctrl.addPreselectedUserRule(_.map(activeMemberships, "user"));
                ctrl.addPreselectedUserRule(
                    _.map(inactiveMemberships, "user"),
                    RULE_INACTIVE_WORKER,
                );
            });
        } else if (_.isUndefined(ctrl.preselectedGrowerMembershipSids)) {
            ctrl.addPreselectedUserRule(ctrl.preselectedUserSids);
        }

        return preselectionPromise
            .then(function () {
                return ctrl.loadData();
            })
            .then(
                function () {
                    ctrl.preselectionLoading = false;
                    return ctrl.recompute();
                },
                function () {
                    ctrl.preselectionLoading = false;
                    ctrl.preselectionLoadFailed = true;
                },
            );
    }

    retryPreselection() {
        let ctrl = this;
        mixpanel.track("Retry employee selection preselection", {
            company_sid: ctrl.grower.sid,
        });
        return ctrl.loadInitialSelection();
    }

    addPreselectedUserRule(userSids, ruleType = RULE_WORKER) {
        let ctrl = this;
        const uniqueUserSids = _.uniq(_.compact(userSids));
        if (!uniqueUserSids.length) {
            return;
        }

        ctrl.includeRules.push({
            type: ruleType,
            workerSids: uniqueUserSids,
        });
    }

    buildRuleSections() {
        let ctrl = this;
        ctrl.ruleSections = [
            {
                key: "include",
                listType: "include",
                rules: ctrl.includeRules,
                headlineTranslation:
                    "employeeSelectionBuilder.INCLUDE_HEADLINE",
                descriptionTranslation: "employeeSelectionBuilder.INCLUDE_DESC",
                buttonTranslationProperty: "includeButtonTranslation",
                ruleToAdd: null,
            },
            {
                key: "exclude",
                listType: "exclude",
                rules: ctrl.excludeRules,
                headlineTranslation:
                    "employeeSelectionBuilder.EXCLUDE_HEADLINE",
                descriptionTranslation: "employeeSelectionBuilder.EXCLUDE_DESC",
                buttonTranslationProperty: "excludeButtonTranslation",
                ruleToAdd: null,
            },
        ];
    }

    loadData() {
        let ctrl = this;
        const loads = _.map(
            _.concat(ctrl.includeRules, ctrl.excludeRules),
            function (rule) {
                return ctrl.loadRuleData(rule);
            },
        );

        return ctrl.$q.all(loads);
    }

    loadRuleData(rule) {
        let ctrl = this;
        const spec = ctrl.getSpec(rule);

        if (!rule) {
            return ctrl.$q.when(ctrl._emptyRuleData);
        }

        if (rule._loadingDataPromise) {
            return rule._loadingDataPromise;
        }

        if (!spec || typeof spec.data !== "function") {
            rule._data = ctrl._emptyRuleData;
            return ctrl.$q.when(rule._data);
        }

        if (rule._data) {
            return ctrl.$q.when(rule._data);
        }

        rule._loadingData = true;
        rule._loadingDataPromise = ctrl.$q
            .when(
                spec.data({
                    rule: rule,
                    growerSid: ctrl.grower.sid,
                }),
            )
            .then(
                function (data) {
                    rule._data = data || ctrl._emptyRuleData;
                    return rule._data;
                },
                function (error) {
                    if (ctrl.rulesOnly) {
                        ctrl.preselectionLoadFailed = true;
                        return ctrl.$q.reject(error);
                    }
                    rule._data = ctrl._emptyRuleData;
                    return rule._data;
                },
            )
            .finally(function () {
                rule._loadingData = false;
                rule._loadingDataPromise = null;
            });

        return rule._loadingDataPromise;
    }

    fetchCrewOptions(growerSid, activeOnly = false) {
        let ctrl = this;
        const params = {
            grower_organization__sid: growerSid,
            ...(activeOnly ? { active: true } : {}),
        };
        return ctrl.$q
            .all([
                ctrl.GroupService.list(params),
                ctrl.FarmService.list(params),
            ])
            .then(function ([crews, farms]) {
                const farmsBySid = _.keyBy(farms, SID_LOOKUP);
                return {
                    crews: crews.map(function (crew) {
                        return {
                            ...crew,
                            farm: farmsBySid[crew.farm],
                        };
                    }),
                };
            });
    }

    fetchOnboardingSet(rule) {
        let ctrl = this;
        let from = rule.fromDate ? moment(rule.fromDate).startOf("day") : null;
        let to = rule.toDate
            ? moment(rule.toDate).startOf("day").add(1, "day")
            : null;

        let params = {
            requester__sid: ctrl.grower.sid,
            cancelled: false,
            omit: "data",
        };
        if (from && from.isValid()) {
            params.created__gte = from.toISOString();
        }
        if (to && to.isValid()) {
            params.created__lt = to.toISOString();
        }

        return ctrl.$q
            .all([
                ctrl.DocumentPackageRequestService.list(params),
                ctrl.GrowerMembershipService.list({
                    grower_organization__sid: ctrl.grower.sid,
                }),
            ])
            .then(function ([requests, growerMemberships]) {
                let membershipToUser = {};
                _.forEach(growerMemberships, function (growerMembership) {
                    if (growerMembership && growerMembership.sid) {
                        membershipToUser[growerMembership.sid] =
                            growerMembership.user;
                    }
                });

                let latestByMembership = {};
                _.forEach(requests, function (request) {
                    if (
                        !request ||
                        !request.grower_membership ||
                        !request.created
                    )
                        return;
                    let createdAt = moment(request.created);
                    if (!createdAt.isValid()) return;
                    let membershipSid = request.grower_membership;
                    let current = latestByMembership[membershipSid];
                    if (!current || createdAt.isAfter(current)) {
                        latestByMembership[membershipSid] = createdAt;
                    }
                });

                let out = new Set();
                _.forEach(latestByMembership, function (_, membershipSid) {
                    let userSid = membershipToUser[membershipSid];
                    if (userSid) out.add(userSid);
                });
                return out;
            });
    }

    getGrowerMembershipActiveRuleValue(rule) {
        if (_.isBoolean(rule.growerMembershipActive)) {
            return rule.growerMembershipActive;
        }
        if (rule.growerMembershipActive === "true") {
            return true;
        }
        if (rule.growerMembershipActive === "false") {
            return false;
        }
        return null;
    }

    /*
     * Rule spec interface for employeeSelectionBuilder:
     *
     * A rule spec defines how a rule is presented, how it loads any data needed
     * to render its UI, and how it turns a configured rule into a user SID set
     * that can be combined with the other include/exclude rules.
     *
     * Each spec object can provide:
     * - type: string, required. Unique identifier for the rule type and the key
     *   used on rule.type.
     * - includeButtonTranslation: string, optional. Translation key shown in the
     *   include "add rule" select.
     * - excludeButtonTranslation: string, optional. Translation key shown in the
     *   exclude "add rule" select.
     * - ruleLabelTranslation: string, optional. Translation key shown above the
     *   rule's inputs in the UI.
     * - buildEmptyRule(): object, optional. Creates a new rule instance for this
     *   spec. The returned object should at minimum contain { type }, plus any
     *   rule-specific UI state such as selected crew SIDs, selected workers,
     *   dates, etc.
     * - data({ rule, growerSid }): object|Promise<object>, optional.
     *   Loads render data needed by this specific rule instance. The return
     *   value is stored on rule._data, so rule instances do not share controller-
     *   wide cached data.
     * - toSet(rule, data): Promise<Set<userSid>>, optional.
     *   Derives the current output set for a rule from the rule instance and its
     *   loaded render data. Rules may still do additional compute-time fetching,
     *   but must always return a promise for the final Set.
     *
     * Rule instance behavior:
     * - Each rule instance owns its own loaded data via rule._data.
     * - In-flight loads are tracked on rule._loadingData and
     *   rule._loadingDataPromise.
     * - Rule-specific ephemeral state can also live on the rule object, such as
     *   rule._latestOnboardingSet.
     * - Rules can keep render data and compute-time data separate. If a rule
     *   does not need remote data to render, its data() hook can stay minimal
     *   while toSet() loads only the compute data it actually needs.
     *
     * Builder expectations:
     * - recompute() is async-aware and waits for every rule's toSet() result.
     * - Include rules are unioned together.
     * - Exclude rules are unioned together.
     * - Final selection is includeUnion - excludeUnion.
     * - Stale recompute results are ignored using a controller-level request id.
     * - Preview is computed separately after the latest selected user SIDs are
     *   emitted.
     *
     * Custom rule specs passed through the ruleSpecs binding should follow this
     * same interface.
     */
    buildRuleSpecs() {
        let ctrl = this;
        const defaultSpecs = [
            {
                type: RULE_CREW,
                id: RULE_CREW,
                includeButtonTranslation:
                    "employeeSelectionBuilder.ADD_INCLUDE_CREW",
                excludeButtonTranslation:
                    "employeeSelectionBuilder.ADD_EXCLUDE_CREW",
                ruleLabelTranslation:
                    "employeeSelectionBuilder.RULE_CREW_LABEL",
                data: ({ growerSid }) => ctrl.fetchCrewOptions(growerSid, true),
                buildEmptyRule: () => ({ type: RULE_CREW, crewSids: [] }),
                toSet: (rule) => {
                    const crewSids = (rule.crewSids || []).map((c) =>
                        typeof c === "string" ? c : c.sid,
                    );
                    if (!crewSids.length) return ctrl.$q.when(new Set());
                    const crewSet = new Set(crewSids);

                    return ctrl.MembershipService.list({
                        active: true,
                        group__grower_organization__sid: ctrl.grower.sid,
                    }).then(function (memberships) {
                        const out = new Set();
                        for (const m of memberships) {
                            if (m.active && crewSet.has(m.group)) {
                                out.add(m.user);
                            }
                        }
                        return out;
                    });
                },
            },
            {
                type: RULE_CREW_HISTORY,
                id: RULE_CREW_HISTORY,
                includeButtonTranslation:
                    "employeeSelectionBuilder.ADD_INCLUDE_CREW_HISTORY",
                excludeButtonTranslation:
                    "employeeSelectionBuilder.ADD_EXCLUDE_CREW_HISTORY",
                ruleLabelTranslation:
                    "employeeSelectionBuilder.RULE_CREW_HISTORY_LABEL",
                data: ({ growerSid }) => ctrl.fetchCrewOptions(growerSid),
                buildEmptyRule: () => ({
                    type: RULE_CREW_HISTORY,
                    crewSids: [],
                    fromDate: null,
                    toDate: null,
                }),
                toSet: (rule) => {
                    const from = moment.utc(rule.fromDate, "YYYY-MM-DD", true);
                    const to = moment.utc(rule.toDate, "YYYY-MM-DD", true);
                    if (!from.isValid() || !to.isValid() || from.isAfter(to)) {
                        return ctrl.$q.when(new Set());
                    }
                    const crewSids = (rule.crewSids || []).map((crew) =>
                        typeof crew === "string" ? crew : crew.sid,
                    );
                    if (!crewSids.length) {
                        return ctrl.$q.when(new Set());
                    }
                    const params = {
                        group__grower_organization__sid: ctrl.grower.sid,
                        created__lt: to
                            .startOf("day")
                            .add(1, "day")
                            .toISOString(),
                        group__sid__in: crewSids.join(","),
                    };
                    return ctrl.$q
                        .all([
                            ctrl.MembershipService.list({
                                ...params,
                                active: true,
                            }),
                            ctrl.MembershipService.list({
                                ...params,
                                active: false,
                                end_date__gte: from
                                    .startOf("day")
                                    .toISOString(),
                            }),
                        ])
                        .then(function (membershipsByStatus) {
                            const users = new Set();
                            for (const memberships of membershipsByStatus) {
                                for (const membership of memberships) {
                                    users.add(membership.user);
                                }
                            }
                            return users;
                        });
                },
            },
            ...[
                {
                    type: RULE_WORKER,
                    includeButtonTranslation:
                        "employeeSelectionBuilder.ADD_INCLUDE_WORKERS",
                    excludeButtonTranslation:
                        "employeeSelectionBuilder.ADD_EXCLUDE_WORKERS",
                    ruleLabelTranslation:
                        "employeeSelectionBuilder.RULE_WORKERS_LABEL",
                },
                {
                    type: RULE_INACTIVE_WORKER,
                    includeButtonTranslation:
                        "employeeSelectionBuilder.ADD_INCLUDE_INACTIVE_WORKERS",
                    excludeButtonTranslation:
                        "employeeSelectionBuilder.ADD_EXCLUDE_INACTIVE_WORKERS",
                    ruleLabelTranslation:
                        "employeeSelectionBuilder.RULE_INACTIVE_WORKERS_LABEL",
                },
            ].map((spec) => ({
                ...spec,
                id: spec.type,
                data: ({ growerSid }) => {
                    return ctrl.GrowerMembershipService.list({
                        grower_organization__sid: growerSid,
                        active: spec.type === RULE_WORKER,
                    }).then(function (growerMemberships) {
                        return {
                            workers: growerMemberships,
                        };
                    });
                },
                buildEmptyRule: () => ({ type: spec.type, workerSids: [] }),
                toSet: (rule) => ctrl.$q.when(new Set(rule.workerSids || [])),
            })),
            {
                type: RULE_GROWER_MEMBERSHIP_ACTIVE,
                id: RULE_GROWER_MEMBERSHIP_ACTIVE,
                includeButtonTranslation:
                    "employeeSelectionBuilder.ADD_INCLUDE_GROWER_MEMBERSHIP_ACTIVE",
                excludeButtonTranslation:
                    "employeeSelectionBuilder.ADD_EXCLUDE_GROWER_MEMBERSHIP_ACTIVE",
                ruleLabelTranslation:
                    "employeeSelectionBuilder.RULE_GROWER_MEMBERSHIP_ACTIVE_LABEL",
                data: () => ctrl._emptyRuleData,
                buildEmptyRule: () => ({
                    type: RULE_GROWER_MEMBERSHIP_ACTIVE,
                    growerMembershipActive: null,
                }),
                toSet: (rule) => {
                    const growerMembershipActive =
                        ctrl.getGrowerMembershipActiveRuleValue(rule);

                    if (!_.isBoolean(growerMembershipActive)) {
                        return ctrl.$q.when(new Set());
                    }

                    return ctrl.GrowerMembershipService.list({
                        grower_organization__sid: ctrl.grower.sid,
                    }).then(function (growerMemberships) {
                        const growerMembershipsByUser = _.groupBy(
                            growerMemberships,
                            "user",
                        );
                        const out = new Set();

                        _.forEach(
                            growerMembershipsByUser,
                            function (_, userSid) {
                                const latestMembership = getLatestMembership(
                                    growerMemberships,
                                    userSid,
                                );
                                if (
                                    latestMembership &&
                                    latestMembership.active ===
                                        growerMembershipActive
                                ) {
                                    out.add(userSid);
                                }
                            },
                        );

                        return out;
                    });
                },
            },
            {
                type: RULE_ONBOARDING,
                id: RULE_ONBOARDING,
                includeButtonTranslation:
                    "employeeSelectionBuilder.ADD_INCLUDE_ONBOARDING",
                excludeButtonTranslation:
                    "employeeSelectionBuilder.ADD_EXCLUDE_ONBOARDING",
                ruleLabelTranslation:
                    "employeeSelectionBuilder.RULE_ONBOARDING_LABEL",
                data: () => ({}),
                buildEmptyRule: () => ({
                    type: RULE_ONBOARDING,
                    fromDate: null,
                    toDate: null,
                }),
                toSet: (rule) => {
                    if (!rule.fromDate && !rule.toDate) {
                        return ctrl.$q.when(new Set());
                    }
                    return ctrl.$q.when(
                        rule._onboardingPromise ||
                            rule._latestOnboardingSet ||
                            new Set(),
                    );
                },
            },
            {
                type: RULE_HOUSING_SITE,
                id: RULE_HOUSING_SITE,
                includeButtonTranslation:
                    "employeeSelectionBuilder.ADD_INCLUDE_HOUSING_SITE",
                excludeButtonTranslation:
                    "employeeSelectionBuilder.ADD_EXCLUDE_HOUSING_SITE",
                ruleLabelTranslation:
                    "employeeSelectionBuilder.RULE_HOUSING_SITE_LABEL",
                data: ({ growerSid }) => {
                    return ctrl.HousingSiteService.list({
                        grower_organization__sid: growerSid,
                    }).then(function (housingSites) {
                        return {
                            housingSites: housingSites,
                        };
                    });
                },
                buildEmptyRule: () => ({
                    type: RULE_HOUSING_SITE,
                    housingSiteSids: [],
                }),
                toSet: (rule) => {
                    const housingSiteSids = (rule.housingSiteSids || []).map(
                        (housingSite) =>
                            typeof housingSite === "string"
                                ? housingSite
                                : housingSite.sid,
                    );
                    if (!housingSiteSids.length) {
                        return ctrl.$q.when(new Set());
                    }
                    const housingSiteSet = new Set(housingSiteSids);

                    return ctrl.$q
                        .all([
                            ctrl.HousingAssignmentService.list({
                                active: true,
                                housing_site__grower_organization__sid:
                                    ctrl.grower.sid,
                            }),
                            ctrl.GrowerMembershipService.list({
                                grower_organization__sid: ctrl.grower.sid,
                            }),
                        ])
                        .then(function ([
                            housingAssignments,
                            growerMemberships,
                        ]) {
                            const growerMembershipBySid = _.keyBy(
                                growerMemberships,
                                SID_LOOKUP,
                            );
                            const out = new Set();

                            for (const assignment of housingAssignments) {
                                if (
                                    assignment.active &&
                                    housingSiteSet.has(assignment.housing_site)
                                ) {
                                    const growerMembership =
                                        growerMembershipBySid[
                                            assignment.grower_membership
                                        ];
                                    if (
                                        growerMembership &&
                                        growerMembership.user
                                    ) {
                                        out.add(growerMembership.user);
                                    }
                                }
                            }

                            return out;
                        });
                },
            },
        ];

        const providedSpecs = Array.isArray(ctrl.ruleSpecs)
            ? ctrl.ruleSpecs.filter((s) => s && (s.type || s.id))
            : [];
        const resolvedSpecs = [];
        const specIndexById = {};

        function getSpecId(spec) {
            if (!spec) {
                return null;
            }

            return spec.id || spec.type || null;
        }

        function addOrOverrideSpec(spec) {
            const specId = getSpecId(spec);
            if (!specId) {
                return;
            }

            if (_.isNumber(specIndexById[specId])) {
                const index = specIndexById[specId];
                resolvedSpecs[index] = _.assign(
                    {},
                    resolvedSpecs[index],
                    spec,
                    {
                        id: specId,
                    },
                );
                return;
            }

            specIndexById[specId] = resolvedSpecs.length;
            resolvedSpecs.push(
                _.assign({}, spec, {
                    id: specId,
                }),
            );
        }

        _.forEach(defaultSpecs, addOrOverrideSpec);
        _.forEach(providedSpecs, addOrOverrideSpec);

        ctrl.resolvedRuleSpecs = resolvedSpecs;
        ctrl._specByType = {};
        for (const spec of ctrl.resolvedRuleSpecs) {
            if (spec && spec.type && !ctrl._specByType[spec.type])
                ctrl._specByType[spec.type] = spec;
        }
    }

    addRule(listType, spec) {
        let ctrl = this;
        if (!spec) return;
        const list =
            listType === "exclude" ? ctrl.excludeRules : ctrl.includeRules;

        mixpanel.track("Add rule in employee selection builder", {
            type: spec.type,
            action: listType,
            company_sid: ctrl.grower.sid,
        });

        const rule = spec.buildEmptyRule
            ? spec.buildEmptyRule()
            : { type: spec.type };
        list.push(rule);
        if (ctrl.rulesOnly) {
            ctrl.loadRuleData(rule).then(
                () => ctrl.recompute(),
                () => {
                    ctrl.preselectionLoadFailed = true;
                    ctrl.setRecomputing(false);
                },
            );
        } else {
            ctrl.loadRuleData(rule);
        }
        ctrl.recompute();
    }

    onAddRuleSelect(section) {
        let ctrl = this;
        if (!section || !section.ruleToAdd) return;

        ctrl.addRule(section.listType, section.ruleToAdd);
        section.ruleToAdd = null;
    }

    removeRule(list, rule) {
        mixpanel.track("Removed rule in employee selection builder", {
            type: rule.type,
        });
        _.remove(list, (r) => r === rule);
        this.recompute();
    }

    onWorkersUpdate(rule, value) {
        // value can be array of user SIDs
        rule.workerSids = Array.isArray(value) ? value : value ? [value] : [];
        this.recompute();
    }

    onCrewHistoryDateChange(rule, field, value) {
        let ctrl = this;
        rule[field] = value;
        return ctrl.recompute();
    }

    onOnboardingDateChange(rule) {
        let ctrl = this;
        if (ctrl.rulesOnly) {
            return ctrl.recompute();
        }
        if (!rule || (!rule.fromDate && !rule.toDate)) {
            rule._latestOnboardingSet = new Set();
            rule._onboardingPromise = null;
            ctrl.recompute();
            return;
        }

        let onboardingPromise = ctrl.fetchOnboardingSet(rule).then(
            function (set) {
                if (rule._onboardingPromise === onboardingPromise) {
                    rule._latestOnboardingSet = set;
                }
                return set;
            },
            function () {
                return new Set();
            },
        );
        rule._onboardingPromise = onboardingPromise;
        ctrl.recompute();
    }

    recompute() {
        let ctrl = this;
        if (ctrl.rulesOnly) {
            if (ctrl.onRulesChange) {
                ctrl.onRulesChange({
                    value: serializeEmployeeSelection(
                        ctrl.includeRules,
                        ctrl.excludeRules,
                    ),
                });
            }
            ctrl.setRecomputing(false);
            return ctrl.$q.when();
        }
        const requestId = ++ctrl._recomputeRequestId;
        ctrl._selectionRequestId++;
        ctrl.setRecomputing(true);

        function unionSets(sets) {
            const out = new Set();
            for (const s of sets) {
                for (const v of s) out.add(v);
            }
            return out;
        }

        function normalizeSet(result) {
            if (result instanceof Set) {
                return result;
            }
            if (_.isArray(result)) {
                return new Set(result);
            }
            if (_.isNil(result)) {
                return new Set();
            }
            return new Set([result]);
        }

        function getRuleDataForSet(rule) {
            if (!rule) {
                return ctrl.$q.when(ctrl._emptyRuleData);
            }

            if (rule._data) {
                return ctrl.$q.when(rule._data);
            }

            if (rule._loadingDataPromise) {
                return rule._loadingDataPromise;
            }

            ctrl.loadRuleData(rule);
            return (
                rule._loadingDataPromise || ctrl.$q.when(ctrl._emptyRuleData)
            );
        }

        function ruleToSet(rule) {
            const spec = ctrl._specByType[rule.type];
            if (spec && typeof spec.toSet === "function") {
                try {
                    return getRuleDataForSet(rule)
                        .then(function (dataToInject) {
                            return spec.toSet(rule, dataToInject);
                        })
                        .then(function (setResult) {
                            return normalizeSet(setResult);
                        })
                        .catch(function () {
                            return new Set();
                        });
                } catch (e) {
                    return ctrl.$q.when(new Set());
                }
            }
            return ctrl.$q.when(new Set());
        }

        return ctrl.$q
            .all([
                ctrl.$q.all(ctrl.includeRules.map(ruleToSet)),
                ctrl.$q.all(ctrl.excludeRules.map(ruleToSet)),
            ])
            .then(function ([includeSets, excludeSets]) {
                if (requestId !== ctrl._recomputeRequestId) {
                    return;
                }

                const includeUnion = unionSets(includeSets);
                const excludeUnion = unionSets(excludeSets);

                // include - exclude
                const final = new Set();
                for (const u of includeUnion) {
                    if (!excludeUnion.has(u)) {
                        final.add(u);
                    }
                }

                const selectedUserSids = Array.from(final);
                return ctrl.$q.when(ctrl.resolveSelection(selectedUserSids));
            })
            .finally(function () {
                if (requestId === ctrl._recomputeRequestId) {
                    ctrl.setRecomputing(false);
                }
            });
    }

    setRecomputing(value) {
        let ctrl = this;
        if (ctrl._recomputing === value) {
            return;
        }

        ctrl._recomputing = value;
        if (ctrl.onRecomputingChange) {
            ctrl.onRecomputingChange({ value });
        }
    }

    resolveSelection(selectedUserSids) {
        let ctrl = this;

        const requestId = ++ctrl._selectionRequestId;

        if (!selectedUserSids.length) {
            ctrl.preview = [];
            ctrl.emit([], []);
            return;
        }

        return ctrl.GrowerMembershipService.severalByLookup(
            selectedUserSids,
            "user__sid__in",
            {
                grower_organization__sid: ctrl.grower.sid,
            },
        )
            .then(function (growerMemberships) {
                if (requestId !== ctrl._selectionRequestId) {
                    return;
                }

                const selectedGrowerMemberships = [];

                for (const sid of selectedUserSids) {
                    const growerMembership =
                        ctrl.preselectedGrowerMembershipByUserSid[sid] ||
                        getLatestMembership(growerMemberships, sid);

                    if (!growerMembership) {
                        continue;
                    }

                    selectedGrowerMemberships.push(growerMembership);
                }

                ctrl.emit(
                    selectedUserSids,
                    _.map(selectedGrowerMemberships, SID_LOOKUP),
                );

                if (!ctrl.showPreview) {
                    ctrl.preview = [];
                    return;
                }

                ctrl.preview = _.map(
                    selectedGrowerMemberships,
                    function (growerMembership) {
                        return {
                            growerMembership: growerMembership,
                        };
                    },
                ).sort((a, b) => {
                    const an =
                        `${a.growerMembership.first_name || ""} ${a.growerMembership.last_name || ""}`.toLowerCase();
                    const bn =
                        `${b.growerMembership.first_name || ""} ${b.growerMembership.last_name || ""}`.toLowerCase();
                    return an.localeCompare(bn);
                });
            })
            .catch(function () {
                if (requestId !== ctrl._selectionRequestId) {
                    return;
                }

                ctrl.preview = [];
                ctrl.emit(selectedUserSids, []);
            });
    }

    emit(value, growerMembershipSids) {
        if (this.onUpdate) {
            this.onUpdate({ value, growerMembershipSids });
        }
    }

    getSpec(rule) {
        return this._specByType[rule.type];
    }

    getSpecData(rule) {
        let ctrl = this;
        if (!rule) {
            return ctrl._emptyRuleData;
        }

        if (rule._data) {
            return rule._data;
        }

        if (!rule._loadingData) {
            ctrl.loadRuleData(rule);
        }

        return ctrl._emptyRuleData;
    }
}

export default {
    bindings: {
        grower: "<",
        onUpdate: "&",
        onRecomputingChange: "&?",
        preselectedUserSids: "<", // optional array of user sids to preselect,
        preselectedGrowerMembershipSids: "<",
        showPreview: "<",
        rulesOnly: "<?",
        selectionRules: "<?",
        onRulesChange: "&?",
        // Optional: array of rule specifications objects.
        // Each object can contain: type (string, required), includeButtonTranslation,
        // excludeButtonTranslation, ruleLabelTranslation, buildEmptyRule() => rule object,
        // toSet(rule, ctx) => Promise<Set<userSid>>
        ruleSpecs: "<",
    },
    controller: EmployeeSelectionBuilderController,
    template,
    name: "employeeSelectionBuilder",
    translations: { en, es },
};
